import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

/* =========================================================
   DATA
   ========================================================= */

/* Hero tech strip. "AI" removed on purpose; SQL now uses a neutral Postgres mark. */
const technologies = [
  {
    name: "AWS",
    icon: "devicon-amazonwebservices-plain-wordmark",
    color: "#FF9900",
  },
  {
    name: "GCP",
    icon: "devicon-googlecloud-plain",
    color: "#4285F4",
  },
  {
    name: "Python",
    icon: "devicon-python-plain",
    color: "#3776AB",
  },
  {
    name: "Spark",
    icon: "devicon-apachespark-plain",
    color: "#E25A1C",
  },
  {
    name: "SQL",
    icon: "devicon-postgresql-plain",
    color: "#5B8FC9",
  },
];

/* The human side. Edit the copy freely: add a favourite player, album, museum, aircraft... */
const interests = [
  {
    key: "football",
    title: "Chelsea FC",
    tag: "football",
    text: "Blue through the highs and the lows. Matchdays are non-negotiable, and so is the post-match debate.",
    color: "#4285F4",
  },
  {
    key: "music",
    title: "Music",
    tag: "always on",
    text: "There's a playlist running whether I'm debugging, commuting or just thinking things through.",
    color: "#A78BFA",
  },
  {
    key: "art",
    title: "Art & history",
    tag: "curious",
    text: "Galleries, museums and the stories behind how the world ended up the way it is.",
    color: "#E3A857",
  },
  {
    key: "jets",
    title: "Private jets",
    tag: "head in the clouds",
    text: "Fascinated by aviation: the engineering, the design, and the dream of the cabin door closing behind you.",
    color: "#4CD8B8",
  },
];

/* The pipeline: raw source data -> something people can use */
const AWS_ICON = {
  icon: "devicon-amazonwebservices-plain-wordmark",
  color: "#FF9900",
};
const GCP_ICON = { icon: "devicon-googlecloud-plain", color: "#4285F4" };

const stages = [
  {
    id: "01",
    label: "Ingest",
    tech: "S3 · GCS · APIs",
    note: "Raw data lands safely, on either cloud",
    icons: [AWS_ICON, GCP_ICON],
    color: "#FF9900",
  },
  {
    id: "02",
    label: "Transform",
    tech: "Python · Spark · SQL",
    note: "Cleaned, validated, modelled",
    icons: [{ icon: "devicon-apachespark-plain", color: "#E25A1C" }],
    color: "#E25A1C",
  },
  {
    id: "03",
    label: "Warehouse",
    tech: "BigQuery",
    note: "Curated, trusted datasets",
    icons: [GCP_ICON],
    color: "#4285F4",
  },
  {
    id: "04",
    label: "Intelligence",
    tech: "AI · ML",
    note: "Insight built on trusted data",
    icons: [{ icon: "devicon-tensorflow-original", color: "#A78BFA" }],
    color: "#A78BFA",
  },
];

const sources = ["REST APIs", "Files", "Databases", "Events"];

const foundation = [
  "Terraform",
  "Docker",
  "CI/CD",
  "Data quality",
  "Observability",
];

/* =========================================================
   ICONS (interests)
   ========================================================= */

function InterestIcon({ name, size = 22 }) {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.7,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  switch (name) {
    case "football":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="m12 8.2 3.4 2.5-1.3 4h-4.2l-1.3-4z" />
          <path d="M12 8.2V3.2M15.4 10.7l4.7-1.6M14.1 14.7l2.9 4M9.9 14.7l-2.9 4M8.6 10.7 3.9 9.1" />
        </svg>
      );
    case "music":
      return (
        <svg {...common}>
          <path d="M9 18V5l11-2v13" />
          <circle cx="6.5" cy="18" r="2.5" />
          <circle cx="17.5" cy="16" r="2.5" />
        </svg>
      );
    case "art":
      return (
        <svg {...common}>
          <rect x="3" y="4" width="18" height="16" rx="2.5" />
          <circle cx="9" cy="10" r="1.6" />
          <path d="m3.5 17 5-4.5 4 3.5 3-2.5 5 4" />
        </svg>
      );
    case "jets":
      return (
        <svg {...common}>
          <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" />
        </svg>
      );
    default:
      return null;
  }
}

/* =========================================================
   PLATFORM DIAGRAM
   Fully flow-based (no absolute positioning), so it stacks
   vertically on phones and runs left-to-right on desktop.
   ========================================================= */

function Connector() {
  return (
    <div
      aria-hidden="true"
      className="relative ml-[3.5rem] h-6 w-px shrink-0 bg-white/10 sm:ml-[3.625rem]"
    >
      <span className="hp-packet" />
    </div>
  );
}

function StageCard({ stage }) {
  const dual = stage.icons.length > 1;

  return (
    <div
      className="
        group relative flex min-w-0 items-center gap-4
        overflow-hidden rounded-2xl border border-white/10
        bg-[#0E131A]/90 p-3.5 backdrop-blur-xl
        transition duration-500
        hover:border-white/20 sm:p-4
      "
      style={{ boxShadow: `0 0 40px ${stage.color}12` }}
    >
      <div
        className="absolute inset-x-0 top-0 h-px opacity-70"
        style={{
          background: `linear-gradient(90deg, transparent, ${stage.color}, transparent)`,
        }}
      />

      {/* Fixed-width icon column keeps every row aligned */}
      <div
        className="flex h-12 w-[5.25rem] shrink-0 items-center justify-center gap-2 rounded-xl border border-white/10 bg-black/30"
        style={{ boxShadow: `inset 0 0 20px ${stage.color}18` }}
      >
        {stage.icons.map((item) => (
          <i
            key={item.icon}
            className={`${item.icon} ${dual ? "text-xl" : "text-[1.7rem]"} transition duration-500 group-hover:scale-110`}
            style={{
              color: item.color,
              filter: `drop-shadow(0 0 8px ${item.color}50)`,
            }}
          />
        ))}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[9px] text-[#59626E]">
            {stage.id}
          </span>
          <p className="text-sm font-semibold tracking-wide text-[#F1F5F3]">
            {stage.label}
          </p>
        </div>

        <p
          className="mt-1 font-mono text-[10px] tracking-wide"
          style={{ color: stage.color }}
        >
          {stage.tech}
        </p>

        <p className="mt-1 text-xs leading-5 text-[#8C96A3]">{stage.note}</p>
      </div>
    </div>
  );
}

function Chip({ children, tone = "default" }) {
  return (
    <span
      className={`rounded-full border px-2.5 py-1 font-mono text-[10px] tracking-wide ${
        tone === "accent"
          ? "border-[#4CD8B8]/25 bg-[#4CD8B8]/5 text-[#4CD8B8]"
          : "border-white/10 bg-white/[0.03] text-[#8C96A3]"
      }`}
    >
      {children}
    </span>
  );
}

function PlatformDiagram() {
  const ref = useRef(null);

  /* Subtle 3D tilt for devices with a real pointer (desktop) */
  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const canHover =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(hover: hover) and (pointer: fine)").matches;

    if (!canHover) return;

    const handleMove = (event) => {
      const rect = element.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      element.style.setProperty(
        "--rotate-y",
        `${(x / rect.width - 0.5) * 5}deg`,
      );
      element.style.setProperty(
        "--rotate-x",
        `${(y / rect.height - 0.5) * -5}deg`,
      );
    };

    const handleLeave = () => {
      element.style.setProperty("--rotate-x", "0deg");
      element.style.setProperty("--rotate-y", "0deg");
    };

    element.addEventListener("mousemove", handleMove);
    element.addEventListener("mouseleave", handleLeave);

    return () => {
      element.removeEventListener("mousemove", handleMove);
      element.removeEventListener("mouseleave", handleLeave);
    };
  }, []);

  return (
    <div className="relative w-full [perspective:1400px]">
      {/* Ambient glow */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#4CD8B8]/10 blur-[110px]" />

      <div
        ref={ref}
        className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#0A0E14]/80 shadow-2xl backdrop-blur-xl transition-transform duration-700 ease-out"
        style={{
          transform:
            "rotateX(var(--rotate-x, 0deg)) rotateY(var(--rotate-y, 0deg))",
        }}
      >
        {/* Dot grid */}
        <div className="pointer-events-none absolute inset-0 opacity-40 [background-image:radial-gradient(rgba(76,216,184,.22)_1px,transparent_1px)] [background-size:26px_26px]" />

        {/* Header */}
        <div className="relative flex items-center justify-between gap-3 border-b border-white/[0.07] px-4 py-3 sm:px-5">
          <div className="min-w-0">
            <p className="text-[11px] text-[#59626E]">What I do</p>
            <p className="mt-0.5 truncate text-sm font-medium text-[#F1F5F3]">
              A data platform, end to end
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-3">
            <div className="hidden items-center gap-1.5 sm:flex">
              <i className="devicon-amazonwebservices-plain-wordmark text-lg text-[#FF9900]" />
              <span className="font-mono text-[9px] text-[#59626E]">+</span>
              <i className="devicon-googlecloud-plain text-base text-[#4285F4]" />
            </div>

            <div className="flex items-center gap-2 rounded-full border border-white/10 bg-[#0E131A]/80 px-2.5 py-1">
              <span className="hp-pulse h-1.5 w-1.5 rounded-full bg-[#4CD8B8] shadow-[0_0_10px_#4CD8B8]" />
              <span className="text-[11px] text-[#8C96A3]">running</span>
            </div>
          </div>
        </div>

        <div className="relative space-y-4 p-4 sm:p-5">
          {/* Sources */}
          <div>
            <p className="mb-2.5 text-[11px] text-[#59626E]">Sources</p>
            <div className="flex flex-wrap gap-2">
              {sources.map((source) => (
                <Chip key={source}>{source}</Chip>
              ))}
            </div>
          </div>

          {/* Flow into the pipeline */}
          <div
            className="-my-1 ml-[4.5rem] sm:ml-[4.875rem]"
            aria-hidden="true"
          >
            <div className="relative h-6 w-px bg-white/10">
              <span className="hp-packet" />
            </div>
          </div>

          {/* Pipeline */}
          <div className="flex flex-col">
            {stages.map((stage, index) => (
              <React.Fragment key={stage.label}>
                <StageCard stage={stage} />
                {index < stages.length - 1 && <Connector />}
              </React.Fragment>
            ))}
          </div>

          {/* Orchestration bar */}
          <div className="relative overflow-hidden rounded-xl border border-dashed border-[#4CD8B8]/25 bg-[#4CD8B8]/[0.04] px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
              <div className="flex items-center gap-2.5">
                <span className="hp-pulse h-1.5 w-1.5 rounded-full bg-[#4CD8B8] shadow-[0_0_10px_#4CD8B8]" />
                <span className="text-xs font-medium text-[#4CD8B8]">
                  Orchestration
                </span>
              </div>
              <span className="font-mono text-[10px] text-[#8C96A3]">
                Airflow · scheduled, retried, observable
              </span>
            </div>
            <span className="hp-sweep" aria-hidden="true" />
          </div>

          {/* Foundation */}
          <div>
            <p className="mb-2.5 text-[11px] text-[#59626E]">
              Platform foundation
            </p>
            <div className="flex flex-wrap gap-2">
              {foundation.map((item) => (
                <Chip key={item} tone="accent">
                  {item}
                </Chip>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Scoped animation */}
      <style>{`
        .hp-packet {
          position: absolute;
          left: 50%;
          top: 0;
          width: 5px;
          height: 5px;
          margin-left: -2.5px;
          border-radius: 999px;
          background: #4CD8B8;
          box-shadow: 0 0 12px #4CD8B8;
          animation: hpFlowY 2.4s linear infinite;
        }

        @keyframes hpFlowY {
          0% { top: -3px; opacity: 0; }
          15% { opacity: 1; }
          85% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }

        .hp-pulse { animation: hpPulse 2.4s ease-in-out infinite; }

        @keyframes hpPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: .45; transform: scale(.8); }
        }

        .hp-sweep {
          position: absolute;
          inset: 0 auto 0 0;
          width: 28%;
          background: linear-gradient(90deg, transparent, rgba(76,216,184,.14), transparent);
          animation: hpSweep 4s ease-in-out infinite;
          pointer-events: none;
        }

        @keyframes hpSweep {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(480%); }
        }

        @media (prefers-reduced-motion: reduce) {
          .hp-packet, .hp-pulse, .hp-sweep { animation: none; }
          .hp-sweep { display: none; }
        }
      `}</style>
    </div>
  );
}

/* =========================================================
   AWAY FROM THE KEYBOARD
   ========================================================= */

function InterestCard({ item }) {
  return (
    <div
      className="
        group relative flex flex-col overflow-hidden
        rounded-2xl border border-white/10 bg-white/[0.025]
        p-5 transition duration-500
        hover:-translate-y-1 hover:border-white/20 hover:bg-white/[0.045]
        sm:p-6
      "
    >
      <div
        className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full opacity-[0.09] blur-3xl transition duration-700 group-hover:scale-150 group-hover:opacity-25"
        style={{ backgroundColor: item.color }}
      />

      <div
        className="relative flex h-12 w-12 items-center justify-center rounded-xl border transition duration-500 group-hover:scale-110"
        style={{
          color: item.color,
          borderColor: `${item.color}40`,
          backgroundColor: `${item.color}12`,
        }}
      >
        <InterestIcon name={item.key} />
      </div>

      <h3 className="relative mt-5 text-base font-semibold text-white">
        {item.title}
      </h3>

      <p
        className="relative mt-0.5 text-xs font-medium"
        style={{ color: item.color }}
      >
        {item.tag}
      </p>

      <p className="relative mt-3 text-sm leading-6 text-[#8C96A3]">
        {item.text}
      </p>

      <div
        className="absolute bottom-0 left-0 h-[2px] w-0 transition-all duration-500 group-hover:w-full"
        style={{ backgroundColor: item.color }}
      />
    </div>
  );
}

function AwayFromKeyboard() {
  return (
    <section className="relative mx-auto w-full max-w-[1550px] px-4 pb-16 sm:px-8 sm:pb-24 lg:px-12">
      <div className="mb-8 flex flex-col gap-4 sm:mb-10 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="mb-3 flex items-center gap-3 text-sm text-[#4CD8B8]">
            <span className="h-px w-8 bg-[#4CD8B8]" />
            Away from the keyboard
          </div>

          <h2
            className="max-w-2xl text-2xl font-semibold leading-tight tracking-tight text-white sm:text-4xl"
            style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
          >
            Engineer by trade, human by default.
          </h2>
        </div>

        <p className="max-w-md text-sm leading-7 text-[#8C96A3] sm:text-right">
          The things that keep me curious (and occasionally keep me up late)
          tend to make me better at the day job too.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
        {interests.map((item) => (
          <InterestCard key={item.key} item={item} />
        ))}
      </div>

      {/* Friendly call to action */}
      <div className="mt-8 flex flex-col items-start justify-between gap-4 rounded-2xl border border-white/10 bg-gradient-to-r from-[#4CD8B8]/[0.07] via-transparent to-[#A78BFA]/[0.06] p-5 sm:flex-row sm:items-center sm:p-6">
        <div>
          <p className="text-base font-semibold text-white">
            Want to talk data pipelines, football or private jets?
          </p>
          <p className="mt-1 text-sm text-[#8C96A3]">
            I'm always up for a good conversation (and a Chelsea debate).
          </p>
        </div>

        <Link
          to="/contact"
          className="group inline-flex shrink-0 items-center justify-center gap-3 rounded-xl bg-[#4CD8B8] px-6 py-3 text-sm font-semibold text-[#080B10] transition duration-300 hover:scale-[1.03] hover:brightness-110"
        >
          Say hello
          <span className="transition-transform duration-300 group-hover:translate-x-1">
            →
          </span>
        </Link>
      </div>
    </section>
  );
}

/* =========================================================
   HOME
   ========================================================= */

export default function Home() {
  return (
    <main className="relative flex min-h-screen flex-col overflow-hidden bg-[#080B10] text-[#F1F5F3]">
      {/* Background */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_25%,rgba(76,216,184,.10),transparent_30%),radial-gradient(circle_at_15%_60%,rgba(66,133,244,.07),transparent_28%),radial-gradient(circle_at_20%_95%,rgba(167,139,250,.06),transparent_25%)]" />

        <div className="absolute inset-0 opacity-30 [background-image:radial-gradient(rgba(255,255,255,.25)_1px,transparent_1px)] [background-size:32px_32px]" />
      </div>

      <section className="relative mx-auto flex w-full max-w-[1550px] items-center px-4 pb-12 pt-24 sm:px-8 sm:py-24 lg:min-h-[calc(100vh-3rem)] lg:px-12">
        <div className="grid w-full items-center gap-12 lg:grid-cols-[.85fr_1.15fr] lg:gap-8">
          {/* CONTENT */}
          <div className="relative z-40 min-w-0">
            <div className="mb-6 flex items-center gap-3 animate-reveal sm:mb-7">
              <span className="h-px w-8 bg-[#4CD8B8] sm:w-10" />

              <span className="text-sm font-medium text-[#4CD8B8]">
                Stephen Adegbile · Cloud data engineer
              </span>
            </div>

            <h1
              className="
                max-w-4xl
                text-[2.6rem]
                font-semibold
                leading-[1]
                tracking-[-.04em]
                sm:text-6xl
                xl:text-7xl
                animate-reveal
              "
              style={{
                fontFamily: "'Space Grotesk', system-ui, sans-serif",
                animationDelay: "100ms",
              }}
            >
              Hi, I'm Stephen.
              <br />
              <span className="text-[#4CD8B8]">I build data systems</span>
              <br />
              <span className="text-[#4CD8B8]">people can trust.</span>
            </h1>

            <p
              className="mt-6 max-w-xl text-[15px] leading-7 text-[#8C96A3] sm:mt-8 sm:text-lg sm:leading-8 animate-reveal"
              style={{ animationDelay: "200ms" }}
            >
              I design cloud data platforms and pipelines across AWS and GCP.
              Away from the keyboard you'll find me watching Chelsea, chasing
              new music, wandering around galleries and museums, or daydreaming
              about private jets.
            </p>

            <div
              className="mt-8 flex flex-col gap-3 sm:mt-9 sm:flex-row sm:flex-wrap animate-reveal"
              style={{ animationDelay: "300ms" }}
            >
              <Link
                to="/about"
                className="group inline-flex items-center justify-center gap-3 rounded-xl bg-[#4CD8B8] px-6 py-3.5 text-sm font-semibold text-[#080B10] transition duration-300 hover:scale-[1.03] hover:brightness-110"
              >
                More about me
                <span className="transition-transform duration-300 group-hover:translate-x-1">
                  →
                </span>
              </Link>

              <a
                href="https://github.com/Stephen0111"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-6 py-3.5 text-sm font-medium backdrop-blur-xl transition duration-300 hover:border-[#4CD8B8]/30 hover:bg-white/[0.06]"
              >
                GitHub ↗
              </a>
            </div>

            {/* TECH STRIP */}
            <div
              className="mt-12 animate-reveal sm:mt-14"
              style={{ animationDelay: "400ms" }}
            >
              <p className="mb-4 text-xs text-[#59626E] sm:mb-5">
                What I work with
              </p>

              <div className="grid grid-cols-3 gap-x-4 gap-y-4 sm:flex sm:flex-wrap sm:items-center sm:gap-x-6">
                {technologies.map((technology) => (
                  <div
                    key={technology.name}
                    className="group flex items-center gap-2 transition duration-300 hover:-translate-y-1"
                  >
                    <i
                      className={`${technology.icon} text-xl sm:text-2xl`}
                      style={{ color: technology.color }}
                    />

                    <span className="font-mono text-[11px] tracking-wider text-[#8C96A3] transition group-hover:text-[#EDEFF2] sm:text-xs">
                      {technology.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* VISUAL */}
          <div className="relative z-10 min-w-0">
            <PlatformDiagram />
          </div>
        </div>
      </section>

      {/* PERSONAL SIDE */}
      <AwayFromKeyboard />

      {/* Footer (in normal flow so it never overlaps content on phones) */}
      <div className="relative mt-auto border-t border-white/[0.06] bg-[#080B10]/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1550px] items-center justify-between gap-4 px-4 py-4 sm:px-8 lg:px-12">
          <div className="flex items-center gap-3">
            <span className="h-1.5 w-1.5 rounded-full bg-[#4CD8B8] shadow-[0_0_10px_#4CD8B8]" />

            <span className="text-xs text-[#8C96A3]">
              © {new Date().getFullYear()} Stephen Adegbile
            </span>
          </div>

          <span className="text-xs text-[#59626E]">Up the Chels </span>
        </div>
      </div>
    </main>
  );
}
